import { ProcessedWaterfallData, WaterfallDataPoint, WaterfallDimension, WaterfallVizData } from './WaterfallChart.types';

export function isNestedWaterfallY(y: WaterfallDataPoint['y']): y is number[][] {
    return Array.isArray(y) && y.length > 0 && Array.isArray(y[0]);
}

export function getWaterfallYRange(point: WaterfallDataPoint, metricIndex: number): [number, number] {
    const { y } = point;
    if (!Array.isArray(y) || y.length === 0) {
        return [0, 0];
    }

    if (isNestedWaterfallY(y)) {
        const metricY = y[metricIndex] ?? y[0];
        if (!Array.isArray(metricY) || metricY.length < 2) {
            return [0, 0];
        }
        return [metricY[0] ?? 0, metricY[1] ?? 0];
    }

    return [Number(y[0] ?? 0), Number(y[1] ?? 0)];
}

export function getWaterfallCategoryLabel(
    point: WaterfallDataPoint,
    dimension: WaterfallDimension,
    isTotalBar: boolean,
): string {
    if (isTotalBar) {
        if (Array.isArray(point.x) && point.x[0]) {
            return String(point.x[0]);
        }
        if (typeof point.x === 'string') {
            return point.x;
        }
        return point.feature ?? point.groupFeature ?? 'Total';
    }

    if (dimension === 'Group Feature' && point.groupFeature) {
        return point.groupFeature;
    }

    if (point.feature) {
        return point.feature;
    }

    if (Array.isArray(point.x) && point.x[0]) {
        return String(point.x[0]);
    }

    return typeof point.x === 'string' ? point.x : 'Unknown';
}

export function isWaterfallTotalBar(
    start: number,
    end: number,
    index: number,
    totalPoints: number,
): boolean {
    return start === 0 || end === 0 || index === 0 || index === totalPoints - 1;
}

export function mergeWaterfallByGroupFeature(
    dataPoints: WaterfallDataPoint[],
    metricIndex: number,
): WaterfallDataPoint[] {
    const merged: WaterfallDataPoint[] = [];
    let index = 0;

    while (index < dataPoints.length) {
        const point = dataPoints[index];
        const [start, end] = getWaterfallYRange(point, metricIndex);
        const isTotal = isWaterfallTotalBar(start, end, index, dataPoints.length);

        if (isTotal || !point.groupFeature) {
            merged.push(point);
            index += 1;
            continue;
        }

        const groupFeature = point.groupFeature;
        let groupEnd = end;
        let nextIndex = index + 1;

        while (nextIndex < dataPoints.length) {
            const nextPoint = dataPoints[nextIndex];
            const [nextStart, nextEnd] = getWaterfallYRange(nextPoint, metricIndex);
            const nextIsTotal = isWaterfallTotalBar(nextStart, nextEnd, nextIndex, dataPoints.length);

            if (nextIsTotal || nextPoint.groupFeature !== groupFeature) {
                break;
            }

            groupEnd = nextEnd;
            nextIndex += 1;
        }

        merged.push({
            ...point,
            x: [groupFeature],
            groupFeature,
            feature: groupFeature,
            y: buildMergedY(point, metricIndex, start, groupEnd),
        });

        index = nextIndex;
    }

    return merged;
}

function buildMergedY(
    point: WaterfallDataPoint,
    metricIndex: number,
    start: number,
    end: number,
): WaterfallDataPoint['y'] {
    if (isNestedWaterfallY(point.y)) {
        return point.y.map((metricY, idx) => {
            if (idx !== metricIndex) {
                return [...metricY] as number[];
            }
            return [start, end];
        });
    }

    return [start, end];
}

function serializeWaterfallDataForSelectionKey(data: WaterfallDataPoint[] | undefined): string {
    if (!data?.length) {
        return '';
    }

    return data
        .map((point) => {
            const xLabel = Array.isArray(point.x) ? point.x.join(',') : point.x;
            return `${xLabel}|${point.groupFeature ?? ''}|${point.feature ?? ''}|${JSON.stringify(point.y)}`;
        })
        .join(';;');
}

export function getWaterfallMetricOptionsKey(vizData: WaterfallVizData): string {
    return resolveWaterfallMetricOptions(vizData)
        .map((option) => `${option.value}:${option.label}`)
        .join('|');
}

export function getWaterfallDimensionOptionsKey(vizData: WaterfallVizData): string {
    return resolveWaterfallDimensionOptions(vizData).join('|');
}

/** Stable fingerprint for when metric/breakdown selections should reset to defaults. */
export function getWaterfallSelectionResetKey(vizData: WaterfallVizData): string {
    return [
        getWaterfallMetricOptionsKey(vizData),
        getWaterfallDimensionOptionsKey(vizData),
        vizData.defaultDimension ?? '',
        serializeWaterfallDataForSelectionKey(vizData.data),
    ].join('::');
}

export function resolveWaterfallMetricOptions(vizData: WaterfallVizData): Array<{ label: string; value: number }> {
    if (vizData.yAxisHeaders?.length) {
        return vizData.yAxisHeaders.map((header, index) => ({
            label: header,
            value: index,
        }));
    }

    const nestedTitles = vizData.yAxisTitle ?? vizData.yAxisTitles?.map((title) => [title]);
    if (nestedTitles?.length) {
        return nestedTitles.map((family, index) => ({
            label: family[0] ?? `Metric ${index + 1}`,
            value: index,
        }));
    }

    return [{ label: 'Value', value: 0 }];
}

export function resolveWaterfallDimensionOptions(vizData: WaterfallVizData): WaterfallDimension[] {
    if (vizData.dimensionOptions?.length) {
        return vizData.dimensionOptions;
    }
    return ['Feature'];
}

/** Prefer top-level Group Feature breakdown when available. */
export function resolveWaterfallDefaultDimension(
    vizData: WaterfallVizData,
    dimensionOptions: WaterfallDimension[],
): WaterfallDimension {
    if (dimensionOptions.includes('Group Feature')) {
        return 'Group Feature';
    }
    if (vizData.defaultDimension && dimensionOptions.includes(vizData.defaultDimension)) {
        return vizData.defaultDimension;
    }
    return dimensionOptions[0] ?? 'Feature';
}

export function resolveWaterfallYAxisTitle(vizData: WaterfallVizData, metricIndex: number): string {
    const nestedTitles = vizData.yAxisTitle ?? vizData.yAxisTitles?.map((title) => [title]);
    const metricTitle = nestedTitles?.[metricIndex]?.[0];
    if (metricTitle) {
        return metricTitle;
    }

    return vizData.yAxisHeaders?.[metricIndex] ?? '';
}

export function processWaterfallData(
    vizData: WaterfallVizData,
    metricIndex: number,
    dimension: WaterfallDimension,
): ProcessedWaterfallData[] {
    const dataPoints = vizData.data ?? [];
    const sourcePoints =
        dimension === 'Group Feature' ? mergeWaterfallByGroupFeature(dataPoints, metricIndex) : dataPoints;

    let baseline = 0;
    for (const point of sourcePoints) {
        const [start, end] = getWaterfallYRange(point, metricIndex);
        if (start !== 0 || end !== 0) {
            baseline = end !== 0 ? end : start;
            break;
        }
    }

    return sourcePoints.map((point, index) => {
        const [start, end] = getWaterfallYRange(point, metricIndex);
        const value = end - start;
        const isTotal = isWaterfallTotalBar(start, end, index, sourcePoints.length);
        const category = getWaterfallCategoryLabel(point, dimension, isTotal);

        let type: ProcessedWaterfallData['type'];
        if (isTotal) {
            type = 'total';
        } else if (value > 0) {
            type = 'increase';
        } else {
            type = 'decrease';
        }

        const percentage = baseline !== 0 ? (Math.abs(value) / Math.abs(baseline)) * 100 : 0;

        return {
            category,
            start,
            end,
            value,
            type,
            percentage,
            displayValue: value.toFixed(2),
            groupFeature: point.groupFeature,
            feature: point.feature,
        };
    });
}
